import {
  buildMaxCharRule,
  buildNoSlashRule,
  buildEmptyValueRule,
  buildNotApplicableRule,
} from './validationRuleBuilders'

// ── ARO-specific compiled patterns ────────────────────────────────────────────
// Common patterns (noSlash, notApplicable, emptyValue) live in validationRuleBuilders.
const namePrefixRe =
  /^(Mr([ .,]|$)|Ms([ .,]|$)|Mrs([ .,]|$)|Mister( |$)|Miss( |$)|Missus( |$))/i
const allowedNameCharsRe = /[^a-zA-Z0-9' -]/
const ssnValidityCheckRe = /(^123-45-6789$)|(^111-11-1111$)|(^333-33-3333$)/
const addressRe = /^(?=[^\d]*\d)(?=[^a-zA-Z]*[a-zA-Z])/

/**
 * Builds the shared ARO validation context.
 *
 * Each field entry is a map of named rules. Every rule is a { re, error } pair:
 * - re:    RegExp compiled once at listener registration, reused across all record evaluations
 * - error: the message passed to record.addError when the rule triggers
 *
 * Audience-specific flags (isRecordkeeper, tpaRequired) centralize all
 * userAudience branching so policy changes only need one edit here.
 *
 * Common rule shapes (maxChar, noSlash, emptyValue, notApplicable) are
 * constructed via helpers from validationRuleBuilders, which other services
 * can import when migrating to the context pattern.
 *
 * @param {string} userAudience - e.g. 'Recordkeeper', 'TPA'
 * @returns {object} Shared validation context for ARO listeners and pure validators
 */
export function buildAroValidationContext(userAudience) {
  const isRecordkeeper = userAudience === 'Recordkeeper'

  return {
    // ── Audience flags ────────────────────────────────────────────────────────
    isRecordkeeper,
    tpaRequired: !isRecordkeeper,

    // ── Pure validator patterns (consumed by ParticipantsDataFormatting) ──
    ssnFormat: /^\d{3}-\d{2}-\d{4}[a-zA-Z]?$/,
    dobFormat: /^\d{4}\/\d{2}\/\d{2}$/,
    zipCodeFormat: /^\d{5}(?:-\d{4})?$/,

    // ── Field validation rules ────────────────────────────────────────────────
    recordkeeper: {
      noSlash: buildNoSlashRule('Recordkeeper'),
      maxChar: buildMaxCharRule(60),
      emptyValue: buildEmptyValueRule('Recordkeeper'),
      notApplicable: buildNotApplicableRule(
        'Please enter a valid Recordkeeper.'
      ),
    },
    tpa: {
      noSlash: buildNoSlashRule('Third Party Administrator'),
      maxChar: buildMaxCharRule(60),
      notApplicable: buildNotApplicableRule(
        'Please enter a valid Third Party Administrator.'
      ),
      emptyValue: buildEmptyValueRule('Third Party Administrator'),
    },
    planSponsor: {
      noSlash: buildNoSlashRule('Plan Sponsor'),
      invalidString: {
        re: /^\s*(Plan Sponsor|Plan Sponsor Name|Company|Company Name|NA|N\/A|Null)\s*$/i,
        error: 'Please type a valid plan sponsor name',
      },
      maxChar: buildMaxCharRule(60),
      emptyValue: buildEmptyValueRule('Plan sponsor'),
    },
    planName: {
      noSlash: buildNoSlashRule('Plan Name'),
      maxChar: buildMaxCharRule(80),
      emptyValue: buildEmptyValueRule('Complete legal plan name'),
    },
    planEin: {
      invalidEINFormat: {
        re: /^\d{2}-\d{7}$/,
        error: 'Format must be: XX-XXXXXXX',
      },
      invalidEIN: {
        re: /^(0[1-6]|1[0-6]|2[0-7]|[35]\d|[468][0-8]|7[1-7]|9[0-589])/,
        error: 'Invalid EIN',
      },
    },
    planNumber: {
      invalidChars: { re: /^(\D)/, error: 'Invalid characters used' },
      mustBeThreeDigits: { re: /^(\d{3})$/, error: 'Must be three digits' },
    },
    planIdNumber: {
      maxChar: buildMaxCharRule(30),
    },
    firstName: {
      maxChar: buildMaxCharRule(40),
      namePrefix: { re: namePrefixRe, error: 'Do not include name prefixes' },
      notApplicable: buildNotApplicableRule(),
      emptyValue: buildEmptyValueRule('First name'),
      allowedNameCharacters: {
        re: allowedNameCharsRe,
        error:
          'Only hyphens and apostrophes are allowed. Periods and commas are not permitted.',
      },
    },
    middleName: {
      maxChar: buildMaxCharRule(20),
      allowedNameCharacters: {
        re: allowedNameCharsRe,
        error:
          'Only hyphens and apostrophes are allowed. Periods and commas are not permitted.',
      },
    },
    lastName: {
      maxChar: buildMaxCharRule(60),
      notApplicable: buildNotApplicableRule(),
      allowedNameCharacters: {
        re: allowedNameCharsRe,
        error:
          'Only hyphens and apostrophes are allowed. Periods and commas are not permitted.',
      },
      emptyValue: buildEmptyValueRule('Last name'),
    },
    addressLine1: {
      maxChar: buildMaxCharRule(36),
      notApplicable: buildNotApplicableRule(),
      emptyValue: buildEmptyValueRule('Address line 1'),
      mustContainNumberAndLetter: {
        re: addressRe,
        error: 'Address must contain both a number and a letter',
      },
    },
    addressLine2: {
      maxChar: buildMaxCharRule(36),
    },
    city: {
      maxChar: buildMaxCharRule(22),
    },
    zipCode: {
      maxChar: buildMaxCharRule(10),
    },
    ssn: {
      validityCheck: {
        re: ssnValidityCheckRe,
        error: 'Given SSN is not valid',
      },
    },
    emailAddress: {
      maxChar: buildMaxCharRule(50),
    },
    traditionalIraPreTaxAmount: {
      maxChar: buildMaxCharRule(14),
    },
    traditionalIraAfterTaxAmount: {
      maxChar: buildMaxCharRule(14),
    },
    rothIraAmount: {
      maxChar: buildMaxCharRule(14),
    },
  }
}
